'use client';

import { NavArrowLeft, NavArrowRight } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { Image } from 'react-datocms/image';
import { CenteredModal } from '~/components/CenteredModal';
import galleryStyles from './ImageGallery.module.css';
import styles from './ImageGalleryCarouselModal.module.css';
import type {
  CarouselModalDatoImage,
  CarouselModalImage,
  ImageGalleryCarouselModalProps
} from './ImageGalleryCarouselModal.types';

const isDatoImage = (image: CarouselModalImage): image is CarouselModalDatoImage => 'data' in image;

const hasValidSrc = (image: CarouselModalImage) => (isDatoImage(image) ? Boolean(image.data?.src) : Boolean(image.src));

const galleryImageKey = (image: CarouselModalImage, index: number) =>
  `${isDatoImage(image) ? image.data.src : image.src}-${index}`;

const MODAL_IMAGE_SIZES = '(min-width: 1024px) 1024px, 100vw';

const renderGalleryImage = (image: CarouselModalImage, className: string, loading: 'eager' | 'lazy' = 'lazy') =>
  isDatoImage(image) ? (
    <Image data={image.data} className={className} layout="fill" objectFit="contain" sizes={MODAL_IMAGE_SIZES} />
  ) : (
    <img
      src={image.src}
      srcSet={image.srcSet}
      sizes={image.srcSet ? MODAL_IMAGE_SIZES : undefined}
      alt={image.alt ?? ''}
      className={className}
      loading={loading}
    />
  );

type ImageGalleryCarouselModalContentProps = {
  images: CarouselModalImage[];
  ariaLabelCarousel?: string;
};

const ImageGalleryCarouselModalContent = ({
  images,
  ariaLabelCarousel
}: Readonly<ImageGalleryCarouselModalContentProps>) => {
  const t = useTranslations('journey');
  const tAria = useTranslations('aria');
  const [modalSlideIndex, setModalSlideIndex] = useState(0);
  const modalScrollRef = useRef<HTMLDivElement>(null);

  const resolved = images.filter(hasValidSrc);
  const carouselLabel = ariaLabelCarousel ?? t('overview_trip_photos');

  const handleModalScroll = () => {
    if (!modalScrollRef.current) return;
    const { scrollLeft, clientWidth } = modalScrollRef.current;
    if (clientWidth > 0) setModalSlideIndex(Math.round(scrollLeft / clientWidth));
  };

  const goToModalSlide = (index: number) => {
    if (!modalScrollRef.current) return;
    modalScrollRef.current.scrollTo({ left: index * modalScrollRef.current.clientWidth, behavior: 'smooth' });
    setModalSlideIndex(index);
  };

  return (
    <div className={styles.modalContent}>
      <section className={styles.modalCarousel} aria-roledescription={tAria('carousel')} aria-label={carouselLabel}>
        <button
          type="button"
          className={clsx(styles.modalArrowButton, styles.modalArrowPrev)}
          onClick={() => goToModalSlide(modalSlideIndex - 1)}
          disabled={modalSlideIndex === 0}
          aria-label={tAria('card_gallery_prev_image')}
        >
          <NavArrowLeft decorative size="large" />
        </button>

        <div ref={modalScrollRef} className={styles.modalScrollTrack} onScroll={handleModalScroll}>
          {resolved.map((image, index) => (
            <div key={`${galleryImageKey(image, index)}-modal`} className={styles.modalSlide}>
              {isDatoImage(image) && image.data.title ? (
                <div
                  className={styles.modalImageFrame}
                  style={{ '--image-aspect-ratio': image.data.aspectRatio } as React.CSSProperties}
                >
                  {renderGalleryImage(image, styles.modalSlideImage, index === 0 ? 'eager' : 'lazy')}
                  <div className={galleryStyles.imageCreditGradient} aria-hidden="true" />
                  <p className={galleryStyles.imageCredit}>{image.data.title}</p>
                </div>
              ) : (
                renderGalleryImage(image, styles.modalSlideImage, index === 0 ? 'eager' : 'lazy')
              )}
            </div>
          ))}
        </div>

        <button
          type="button"
          className={clsx(styles.modalArrowButton, styles.modalArrowNext)}
          onClick={() => goToModalSlide(modalSlideIndex + 1)}
          disabled={modalSlideIndex === resolved.length - 1}
          aria-label={tAria('card_gallery_next_image')}
        >
          <NavArrowRight decorative size="large" />
        </button>
      </section>

      {resolved.length > 1 ? (
        <ul className={styles.modalDots} aria-label={t('itinerary_gallery_slides')}>
          {resolved.map((image, i) => (
            <li key={`${galleryImageKey(image, i)}-modal-dot`}>
              <button
                type="button"
                className={clsx(styles.modalDot, i === modalSlideIndex && styles.modalDotActive)}
                onClick={() => goToModalSlide(i)}
                aria-label={tAria('card_gallery_go_to_slide', { slide: i + 1 })}
                aria-current={i === modalSlideIndex ? 'true' : undefined}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export const ImageGalleryCarouselModal = ({
  images,
  isOpen,
  onClose,
  ariaLabelCarousel,
  closeDataTestId = 'ImageGalleryCarouselClose',
  dataTestId = 'ImageGalleryCarouselOverlay'
}: Readonly<ImageGalleryCarouselModalProps>) => {
  const tAria = useTranslations('aria');
  const resolved = images.filter(hasValidSrc);

  if (resolved.length === 0) return null;

  return (
    <CenteredModal
      ariaLabel={tAria('image_gallery_modal')}
      close={onClose}
      closeDataTestId={closeDataTestId}
      dataTestId={dataTestId}
      isOpen={isOpen}
      size="large"
    >
      {isOpen ? <ImageGalleryCarouselModalContent images={resolved} ariaLabelCarousel={ariaLabelCarousel} /> : null}
    </CenteredModal>
  );
};
